import type { CurrencyCode } from '@faststore/sdk'
import type { CartItem } from 'src/sdk/cart/validate'
import { getDynamicYieldConsent } from 'src/utils/dynamicYieldConsent'

type DynamicYieldCartItem = {
  productId: string
  quantity: number
  itemPrice: number
}

export type DynamicYieldLoginParams = {
  cuid: string
  cuidType: string
}

const toCurrencyValue = (value: number, unitMultiplier: number) => Math.round(value * unitMultiplier * 100) / 100

const trackDynamicYieldEvent = (
  name: string,
  properties: Record<string, unknown>
) => {
  if (typeof window === 'undefined') {
    return false
  }

  // Gli script DY possono essere caricati anche senza consenso per mostrare
  // campagne consentless. Gli eventi comportamentali custom, invece, partono
  // solo se CookieBot ha abilitato la categoria collegata a Dynamic Yield.
  if (!getDynamicYieldConsent()) {
    return false
  }

  if (!window.DY?.API) {
    return false
  }

  window.DY.API('event', {
    name,
    properties,
  })

  return true
}

export const mapCartItemToDYProductId = (item: CartItem) =>
  item.itemOffered.sku ??
  item.itemOffered.gtin ??
  item.itemOffered.isVariantOf?.productGroupID ??
  item.id

export const mapCartItemToDYCartItem = (
  item: CartItem
): DynamicYieldCartItem | null => {
  const productId = mapCartItemToDYProductId(item)

  if (!productId) {
    return null
  }

  return {
    productId: String(productId),
    quantity: item.quantity,
    itemPrice: toCurrencyValue(item.price, 1),
  }
}

export const mapCartToDYCart = (cartItems: CartItem[]) =>
  cartItems
    .map(mapCartItemToDYCartItem)
    .filter(Boolean) as DynamicYieldCartItem[]

type DynamicYieldAddToCartItem = {
  item_id?: string | number | null
  item_variant?: string | number | null
  product_reference_id?: string | number | null
  quantity?: number
  price?: number
  unitMultiplier: number
}

export type DynamicYieldAddToCartParams = {
  items?: DynamicYieldAddToCartItem[]
  dynamicYield?: {
    currency?: CurrencyCode
    value?: number
    // Units added by this event, when different from items[0].quantity
    quantity?: number
    // True when items[0].price is already the price of the whole pack (e.g.
    // Algolia selling_price), so the unit multiplier must not be applied again
    priceIncludesUnitMultiplier?: boolean
    cartItems?: CartItem[]
  }
}

type DynamicYieldAnalyticsEvent = {
  name: string
  params?: unknown
}

type DynamicYieldEventHandler = (params: unknown) => void

const mapAnalyticsItemToDYProductId = (item: DynamicYieldAddToCartItem) =>
  item.item_variant ?? item.item_id ?? item.product_reference_id

const mapAnalyticsItemToDYCartItem = (
  item: DynamicYieldAddToCartItem
): DynamicYieldCartItem | null => {
  const productId = mapAnalyticsItemToDYProductId(item)

  if (!productId || typeof item.price !== 'number') {
    return null
  }

  return {
    productId: String(productId),
    quantity: item.quantity ?? 1,
    itemPrice: toCurrencyValue(item.price, item.unitMultiplier),
  }
}

const mapCartToDYCartAfterAdd = (
  cartItems: CartItem[],
  addedItem: DynamicYieldAddToCartItem,
  addedQuantity: number
) => {
  const cart = mapCartToDYCart(cartItems)
  const addedCartItem = mapAnalyticsItemToDYCartItem({
    ...addedItem,
    quantity: addedQuantity,
  })

  if (!addedCartItem) {
    return cart
  }

  const existingCartItemIndex = cart.findIndex(
    ({ productId }) => productId === addedCartItem.productId
  )

  if (existingCartItemIndex >= 0) {
    cart[existingCartItemIndex] = {
      ...cart[existingCartItemIndex],
      quantity: cart[existingCartItemIndex].quantity + addedCartItem.quantity,
      itemPrice: addedCartItem.itemPrice,
    }

    return cart
  }

  return [...cart, addedCartItem]
}

export const trackDYAddToCart = (params: DynamicYieldAddToCartParams) => {
  const { currency, value, quantity, cartItems, priceIncludesUnitMultiplier } =
    params.dynamicYield ?? {}
  const firstItem = params.items?.[0]
  const item =
    firstItem && priceIncludesUnitMultiplier
      ? { ...firstItem, unitMultiplier: 1 }
      : firstItem

  if (!item || !currency || typeof value !== 'number') {
    return
  }

  const productId = mapAnalyticsItemToDYProductId(item)

  if (!productId) {
    return
  }

  const addedQuantity = quantity ?? item.quantity ?? 1

  trackDynamicYieldEvent('Add to Cart', {
    dyType: 'add-to-cart-v1',
    value: toCurrencyValue(value, item.unitMultiplier),
    currency,
    productId: String(productId),
    quantity: addedQuantity,
    cart: mapCartToDYCartAfterAdd(cartItems ?? [], item, addedQuantity),
  })
}

export const trackDYLogin = ({ cuid, cuidType }: DynamicYieldLoginParams) => {
  const loginCuid = cuid.trim()

  if (!loginCuid || loginCuid.includes('/') || loginCuid.length > 250) {
    return false
  }

  return trackDynamicYieldEvent('Login', {
    dyType: 'login-v1',
    cuid: loginCuid,
    cuidType,
  })
}

const dynamicYieldEventHandlers: Record<string, DynamicYieldEventHandler> = {
  add_to_cart: (params) =>
    trackDYAddToCart(params as DynamicYieldAddToCartParams),
  // purchase: (params) => trackDYPurchase(params as DynamicYieldPurchaseParams),
}

export const trackDynamicYieldAnalyticsEvent = (
  event: DynamicYieldAnalyticsEvent
) => {
  const handler = dynamicYieldEventHandlers[event.name]

  handler?.(event.params)
}
